import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBan,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaFilter,
  FaSearch,
  FaSortAmountDown,
  FaUser,
} from "react-icons/fa";
import {
  getAgentMerchants,
  getAgentMerchantsStats,
  getAllMerchants,
} from "../api/api";
import "../styles/MerchantsPage.css";

const PAGE_SIZE = 10;

const FALLBACK_MERCHANTS = [
  {
    _id: "m1",
    merchantID: "MF-98231",
    merchantName: "Velvet Coffee Co.",
    dbaName: "Velvet Coffee Co.",
    status: "active",
    assignedAgent: "James Wilson",
    staticQr: 4200,
    dynamicQr: 12800,
    volume: 412500,
    lastActivity: "4 mins ago",
    lastNote: "Processing...",
    categoryName: "Food & Beverage",
    region: "EMEA",
    healthScore: 88,
  },
  {
    _id: "m2",
    merchantID: "MF-10255",
    merchantName: "Urban Fit Ltd.",
    dbaName: "Urban Fit Ltd.",
    status: "pending",
    assignedAgent: "Sarah Jenkins",
    staticQr: 152,
    dynamicQr: 0,
    volume: 12240,
    lastActivity: "Yesterday",
    lastNote: "Verification pending",
    categoryName: "Retail",
    region: "EMEA",
    healthScore: 45,
  },
  {
    _id: "m3",
    merchantID: "MF-88712",
    merchantName: "LuxuStyle Boutique",
    dbaName: "LuxuStyle Boutique",
    status: "blocked",
    assignedAgent: "James Wilson",
    staticQr: 1100,
    dynamicQr: 420,
    volume: 89000,
    lastActivity: "3 days ago",
    lastNote: "Chargeback alert",
    categoryName: "Fashion",
    region: "EMEA",
    healthScore: 15,
  },
];

const normalizeStatus = (status = "") => {
  const raw = String(status).toLowerCase();
  if (["inactive", "failed", "disabled"].includes(raw)) return "blocked";
  if (["pending", "updated", "review"].includes(raw)) return "pending";
  return "active";
};

const parseVolume = (value) => {
  if (typeof value === "number") return value;
  const numeric = Number(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatMoney = (value) => `Rs ${Number(value || 0).toLocaleString()}`;
const formatCompactMoney = (value) => {
  const n = Number(value || 0);
  if (n >= 1000000) return `Rs ${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `Rs ${(n / 1000).toFixed(1)}K`;
  return `Rs ${n.toLocaleString()}`;
};

const formatQr = (value) => {
  const n = Number(value || 0);
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const normalizeMerchant = (merchant, idx, agentName = "") => {
  const status = normalizeStatus(merchant.status);
  return {
    ...merchant,
    _id: merchant._id || merchant.merchantID || `m-${idx}`,
    merchantID: merchant.merchantID || merchant.MID || `MF-${90000 + idx}`,
    merchantName: merchant.merchantName || merchant.name || "Unknown Merchant",
    dbaName: merchant.dbaName || merchant.merchantName || "—",
    status,
    assignedAgent:
      merchant.assignedAgent ||
      merchant.agentName ||
      merchant.agent ||
      agentName ||
      "Unassigned",
    staticQr: Number(merchant.staticQr ?? merchant.staticQrCount ?? merchant.staticCount ?? 0),
    dynamicQr: Number(merchant.dynamicQr ?? merchant.dynamicQrCount ?? merchant.dynamicCount ?? 0),
    volume: parseVolume(merchant.volume ?? merchant.totalVolume ?? merchant.monthlyVolume),
    lastActivity: merchant.lastActivity || merchant.lastTxnAt || merchant.updatedAt || "—",
    lastNote: merchant.lastNote || "Verification pending",
    categoryName: merchant.categoryName || merchant.category || "General",
    region: merchant.region || merchant.country || "EMEA",
    healthScore: Number(merchant.healthScore ?? (status === "active" ? 86 : status === "pending" ? 47 : 16)),
  };
};

const AgentMerchantsDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const agentData = location.state?.user || {};
  const agentId = agentData?._id;
  const isAgentMode = Boolean(agentId);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rawMerchants, setRawMerchants] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [region, setRegion] = useState("All");
  const [sort, setSort] = useState("Newest");
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let merchantRows = [];
        let fetchedStats = null;

        if (isAgentMode) {
          const [statsRes, merchantsRes] = await Promise.all([
            getAgentMerchantsStats(agentId),
            getAgentMerchants(agentId, new URLSearchParams({ page: 1, limit: 500 })),
          ]);

          if (statsRes?.success) fetchedStats = statsRes.data;
          if (merchantsRes?.success && Array.isArray(merchantsRes.data)) {
            merchantRows = merchantsRes.data;
          }
        } else {
          const res = await getAllMerchants();
          if (Array.isArray(res?.data)) merchantRows = res.data;
          else if (Array.isArray(res?.merchants)) merchantRows = res.merchants;
          else if (Array.isArray(res)) merchantRows = res;
        }

        const normalized = (merchantRows.length ? merchantRows : FALLBACK_MERCHANTS).map((m, idx) =>
          normalizeMerchant(m, idx, agentData?.name)
        );
        setRawMerchants(normalized);

        if (fetchedStats) {
          setStats({
            total: Number(fetchedStats.total ?? normalized.length),
            active: Number(fetchedStats.active ?? normalized.filter((m) => m.status === "active").length),
            blocked: Number(
              fetchedStats.inactive ??
                fetchedStats.disabled ??
                normalized.filter((m) => m.status === "blocked").length
            ),
          });
        }
      } catch (error) {
        console.error("Failed loading merchants:", error);
        setRawMerchants(FALLBACK_MERCHANTS);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [agentData?.name, agentId, isAgentMode]);

  const categories = useMemo(
    () => ["All", ...new Set(rawMerchants.map((m) => m.categoryName).filter(Boolean))],
    [rawMerchants]
  );

  const regions = useMemo(
    () => ["All", ...new Set(rawMerchants.map((m) => m.region).filter(Boolean))],
    [rawMerchants]
  );

  const filteredMerchants = useMemo(() => {
    const q = search.trim().toLowerCase();

    let rows = rawMerchants.filter((m) => {
      const matchesSearch =
        !q ||
        m.merchantName.toLowerCase().includes(q) ||
        m.merchantID.toLowerCase().includes(q) ||
        String(m.assignedAgent).toLowerCase().includes(q);

      const matchesCategory = category === "All" || m.categoryName === category;
      const matchesRegion = region === "All" || m.region === region;
      const matchesTab =
        activeTab === "all" ||
        (activeTab === "approval" && m.status === "pending") ||
        (activeTab === "tiers" && m.healthScore >= 60);

      return matchesSearch && matchesCategory && matchesRegion && matchesTab;
    });

    rows = [...rows].sort((a, b) => {
      if (sort === "Highest Volume") return b.volume - a.volume;
      if (sort === "Health") return b.healthScore - a.healthScore;
      if (sort === "Oldest") return String(a.merchantID).localeCompare(String(b.merchantID));
      return String(b.merchantID).localeCompare(String(a.merchantID));
    });

    return rows;
  }, [activeTab, category, rawMerchants, region, search, sort]);

  const totalCount = filteredMerchants.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredMerchants.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [activeTab, category, region, search, sort]);

  const aggregate = useMemo(() => {
    const total = rawMerchants.length;
    const active = rawMerchants.filter((m) => m.status === "active").length;
    const blocked = rawMerchants.filter((m) => m.status === "blocked").length;
    const volume = rawMerchants.reduce((sum, m) => sum + m.volume, 0);
    const avgTicket = total ? Math.round(volume / total / 20) : 0;

    return {
      total,
      active,
      blocked,
      volume,
      avgTicket,
      excellent: rawMerchants.filter((m) => m.healthScore >= 80).length,
      good: rawMerchants.filter((m) => m.healthScore >= 60 && m.healthScore < 80).length,
      risk: rawMerchants.filter((m) => m.healthScore >= 35 && m.healthScore < 60).length,
      critical: rawMerchants.filter((m) => m.healthScore < 35).length,
    };
  }, [rawMerchants]);

  const approvalQueue = useMemo(
    () => rawMerchants.filter((m) => m.status === "pending" || m.status === "blocked").slice(0, 3),
    [rawMerchants]
  );

  const headerStats = [
    {
      label: "Total Merchants",
      value: Number(stats?.total ?? aggregate.total).toLocaleString(),
      trend: "+ 5.2%",
      tone: "mint",
    },
    {
      label: "Active",
      value: Number(stats?.active ?? aggregate.active).toLocaleString(),
      trend: "92.3% Retention",
      tone: "cyan",
    },
    {
      label: "Blocked",
      value: Number(stats?.blocked ?? aggregate.blocked).toLocaleString(),
      trend: "-2 since last week",
      tone: "rose",
    },
    {
      label: "Trans. Volume",
      value: formatCompactMoney(aggregate.volume),
      trend: "+12% MoM",
      tone: "mint",
    },
    {
      label: "Avg Ticket",
      value: formatMoney(aggregate.avgTicket),
      trend: "Optimized range",
      tone: "amber",
    },
  ];

  return (
    <div className="mp-page">
      <div className="mp-head">
        <div>
          <h1 className="mp-title">Merchants</h1>
          <p className="mp-sub">
            {isAgentMode ? `${agentData?.name || "Selected Agent"} portfolio` : "Organization merchant network overview"}
          </p>
        </div>

        <div className="mp-search">
          <FaSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search merchants, IDs, or agents..."
          />
        </div>
      </div>

      <div className="mp-kpis">
        {headerStats.map((item) => (
          <div key={item.label} className="mp-kpi">
            <div className="mp-kpi__label">{item.label}</div>
            <div className="mp-kpi__value">{loading ? "—" : item.value}</div>
            <div className={`mp-kpi__trend mp-kpi__trend--${item.tone}`}>{item.trend}</div>
          </div>
        ))}
      </div>

      <div className="mp-toolbar">
        <div className="mp-tabs">
          <button className={activeTab === "all" ? "active" : ""} onClick={() => setActiveTab("all")}>All Merchants</button>
          <button className={activeTab === "approval" ? "active" : ""} onClick={() => setActiveTab("approval")}>
            Approval Queue ({approvalQueue.length})
          </button>
          <button className={activeTab === "tiers" ? "active" : ""} onClick={() => setActiveTab("tiers")}>Performance Tiers</button>
        </div>

        <div className="mp-controls">
          <label>
            <FaFilter />
            <span>Category:</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Region:</span>
            <select value={region} onChange={(e) => setRegion(e.target.value)}>
              {regions.map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            </select>
          </label>
          <label>
            <FaSortAmountDown />
            <span>Sort:</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              {["Newest", "Oldest", "Highest Volume", "Health"].map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="mp-tablecard">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Merchant</th>
              <th>Status &amp; Health</th>
              <th>Assigned Agent</th>
              <th>QR Usage</th>
              <th>Volume</th>
              <th>Last Activity</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="mp-empty">Loading merchants...</td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="mp-empty">No merchants match current filters.</td>
              </tr>
            ) : (
              pageRows.map((merchant) => (
                <tr key={merchant._id}>
                  <td>
                    <div className="mp-merchant">
                      <div className="mp-merchant__avatar">{merchant.merchantName.slice(0, 1)}</div>
                      <div>
                        <div className="mp-merchant__name">{merchant.merchantName}</div>
                        <div className="mp-merchant__id">ID: {merchant.merchantID}</div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className={`mp-status mp-status--${merchant.status}`}>{merchant.status}</div>
                    <div className="mp-health">
                      <span>{merchant.healthScore}</span>
                    </div>
                  </td>

                  <td className="mp-agent">{merchant.assignedAgent}</td>

                  <td>
                    <div className="mp-qr">
                      <div>
                        <b>{formatQr(merchant.staticQr)}</b>
                        <small>static</small>
                      </div>
                      <div>
                        <b>{formatQr(merchant.dynamicQr)}</b>
                        <small>dynamic</small>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="mp-volume">{formatMoney(merchant.volume)}</div>
                    <small>{merchant.dynamicQr + merchant.staticQr} txns</small>
                  </td>

                  <td>
                    <div className="mp-last">{merchant.lastActivity}</div>
                    <small>{merchant.lastNote}</small>
                  </td>

                  <td>
                    <div className="mp-actions">
                      <button
                        title="Open Profile"
                        onClick={() =>
                          navigate("/dashboard/merchantprofile", {
                            state: { merchant, name: merchant.merchantName },
                          })
                        }
                      >
                        <FaUser />
                      </button>
                      <button
                        title="Transactions"
                        onClick={() =>
                          navigate("/dashboard/merchant-transactions", {
                            state: { merchant, name: merchant.merchantName },
                          })
                        }
                      >
                        <FaEdit />
                      </button>
                      <button title="Block / Suspend">
                        <FaBan />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && (
          <div className="mp-tablefoot">
            <div>
              Showing {(currentPage - 1) * PAGE_SIZE + (totalCount ? 1 : 0)} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} merchants
            </div>

            <div className="mp-pager">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <FaChevronLeft />
              </button>
              {Array.from({ length: Math.min(totalPages, 3) }, (_, idx) => idx + 1).map((n) => (
                <button key={n} className={n === currentPage ? "active" : ""} onClick={() => setPage(n)}>
                  {n}
                </button>
              ))}
              {totalPages > 3 && <span>...</span>}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mp-bottom">
        <section className="mp-card">
          <div className="mp-card__head">
            <h3>Approval Queue</h3>
            <span>{approvalQueue.length} new</span>
          </div>

          <div className="mp-queue">
            {approvalQueue.length === 0 ? (
              <div className="mp-empty-small">No pending approvals.</div>
            ) : (
              approvalQueue.map((merchant) => (
                <button key={merchant._id} className="mp-queue__item" type="button">
                  <div>
                    <strong>{merchant.merchantName}</strong>
                    <small>{merchant.categoryName}</small>
                  </div>
                  <span>›</span>
                </button>
              ))
            )}
          </div>

          <button className="mp-card__link" type="button">View Full Queue</button>
        </section>

        <section className="mp-card mp-card--wide">
          <div className="mp-card__head">
            <h3>Merchant Health Distribution</h3>
            <span>Last 30 Days</span>
          </div>

          <div className="mp-healthbars">
            <div>
              <span style={{ height: `${Math.max(8, aggregate.excellent * 14)}px` }} />
              <p>Excellent ({aggregate.excellent})</p>
            </div>
            <div>
              <span style={{ height: `${Math.max(8, aggregate.good * 14)}px` }} />
              <p>Good ({aggregate.good})</p>
            </div>
            <div>
              <span style={{ height: `${Math.max(8, aggregate.risk * 14)}px` }} />
              <p>At Risk ({aggregate.risk})</p>
            </div>
            <div>
              <span style={{ height: `${Math.max(8, aggregate.critical * 14)}px` }} />
              <p>Critical ({aggregate.critical})</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AgentMerchantsDetails;
